import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { ApiConfig } from '@/config/api.config.ts';
import { useAxiosAuth } from '@/hooks/useAxiosAuth.ts';
import { ProfileDto } from '@/features/account/types/account-dto.ts';
import { useIsAuthenticated } from '@/features/identity/hooks/useIsAuthenticated.ts';
import EToast from '@/components/everridge/EToast';

const getApiErrorDescription = (error: unknown, fallback: string): string => {
    if (!isAxiosError(error) || !error.response?.data) return fallback;

    const data = error.response.data as Record<string, unknown>;
    if (typeof data.detail === 'string' && data.detail.length > 0) return data.detail;

    if (data.errors && typeof data.errors === 'object') {
        const errors = data.errors as Record<string, string[]>;
        const firstMessage = Object.values(errors).flat().find(Boolean);
        if (firstMessage) return firstMessage;
    }

    return fallback;
};

export const useProfileApi = () => {
    const axiosInstance = useAxiosAuth();
    const isAuthenticated = useIsAuthenticated();
    const { t } = useTranslation();

    const { data: profile, isLoading: isProfileLoading } = useQuery({
        queryKey: [ApiConfig.queries.features.accounts.queryKeys.myProfile],
        queryFn: async () => {
            const url = ApiConfig.queries.features.accounts.endpoints.myProfile;
            try {
                const response = await axiosInstance.get<ProfileDto>(url);
                return response.data;
            } catch (error) {
                EToast({
                    variant: 'error',
                    message: t('main.account.profileLoad.error.message'),
                    description: getApiErrorDescription(
                        error,
                        t('main.account.profileLoad.error.description'),
                    ),
                });
                throw error;
            }
        },
        enabled: isAuthenticated,
    });

    return {
        profile,
        isProfileLoading,
    };
};
